import React, { useState } from "react";
import toast from "react-hot-toast";
import { getPractitionerHealthID } from "../nhicApi";
import { useTranslation } from "react-i18next";
import AppModal from "../../../components/AppModal";
import AppButton from "../../../shared/AppButton";
import CustomFilterInput from "../../../shared/CustomFilterInput";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import BadgeIcon from "@mui/icons-material/Badge";
import VisibilityIcon from "@mui/icons-material/Visibility";

const InfoItem = ({ label, value, highlight = false, full = false }) => (
  <div
    className={`rounded-md border border-slate-200 bg-white px-2 py-1.5 ${
      full ? "md:col-span-2" : ""
    }`}
  >
    <p className="text-[10px] font-bold uppercase text-slate-500">{label}</p>
    <p
      className={`mt-0.5 break-words text-[12px] font-semibold ${
        highlight ? "text-[#0070C2]" : "text-slate-700"
      }`}
    >
      {value || "---"}
    </p>
  </div>
);

function PractitionerHealthIDLookup() {
  const { t } = useTranslation();
  const [practitionerId, setPractitionerId] = useState("");
  const [result, setResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!practitionerId) {
      toast.error(t("Please enter a practitioner ID"));
      return;
    }

    try {
      setLoading(true);
      const response = await getPractitionerHealthID(practitionerId);
      setResult(response?.data || response);
      setShowResultModal(true);
      toast.success(
        response?.message || t("Practitioner health ID retrieved successfully!"),
      );
    } catch (error) {
      toast.error(
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error.message ||
          t("An error occurred while fetching practitioner health ID"),
      );
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setPractitionerId("");
    setResult(null);
    setShowResultModal(false);
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="mb-3 flex items-center gap-2 border-b border-slate-100 pb-2">
        <BadgeIcon sx={{ fontSize: 19, color: "#0070C2" }} />
        <h2 className="text-[14px] font-bold text-slate-700">
          {t("Practitioner Health ID Lookup")}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <CustomFilterInput
          label={`${t("Practitioner ID")} *`}
          name="practitionerId"
          value={practitionerId}
          onChange={(e) => setPractitionerId(e.target.value)}
          placeholder="1599999983"
          disabled={loading}
        />

        <div className="flex flex-wrap justify-end gap-2">
          {result && (
            <AppButton
              label={t("Reset")}
              variant="outlined"
              onClick={handleReset}
              startIcon={<RestartAltIcon />}
              disabled={loading}
              className="!border-gray-300 !text-gray-600 hover:!bg-gray-100"
            />
          )}
          {result && (
            <AppButton
              label={t("View Result")}
              variant="outlined"
              onClick={() => setShowResultModal(true)}
              startIcon={<VisibilityIcon />}
              disabled={loading}
              className="!border-primary !text-primary hover:!bg-primary/10"
            />
          )}
          <AppButton
            label={t("Lookup Health ID")}
            type="submit"
            startIcon={<SearchIcon />}
            isLoading={loading}
            loadingText={t("Searching")}
          />
        </div>
      </form>

      <AppModal
        open={Boolean(showResultModal && result)}
        onClose={() => setShowResultModal(false)}
        title={t("Practitioner Information")}
        maxWidth="md"
        contentSx={{ backgroundColor: "#f8fafc" }}
      >
        {result && (
          <div className="rounded-md border border-[#0070C2]/20 bg-[#0070C2]/5 p-3">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-[13px] font-bold text-slate-700">
              {t("Practitioner Information")}
            </h3>
          </div>

          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <InfoItem label={t("Health ID")} value={result.health_id} highlight />
            <InfoItem label={t("Practitioner ID")} value={result.practitioner_id} />
            <InfoItem label={t("Name")} value={result.name} />
            <InfoItem label={t("License Number")} value={result.license_number} />
            <InfoItem label={t("Speciality")} value={result.speciality} full />
          </div>

          {result.additional_info && (
            <div className="mt-2 border-t border-[#0070C2]/20 pt-2">
              <p className="mb-1 text-[10px] font-bold uppercase text-slate-500">
                {t("Additional Information")}
              </p>
              <pre className="max-h-48 overflow-auto rounded-md bg-white p-2 text-[11px] text-slate-700">
                {JSON.stringify(result.additional_info, null, 2)}
              </pre>
            </div>
          )}
        </div>
        )}
      </AppModal>
    </div>
  );
}

export default PractitionerHealthIDLookup;
